import React, { useState, useCallback, useEffect, useRef } from "react";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import { debounce } from "lodash";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  FaFilter,
  FaTimes,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFilePdf,
} from "react-icons/fa";
import "./DataTable.css";

const DataTable = ({
  data, // The actual data
  ignoredAttributes = [], // An array of data attributes we don't want to render
  type = "default", // the type parameter is used for specific actions for specific reasons.
  columnMapping = {}, // This param is used to change the name of columns
  onRowClick, // This function occurs when the user clicks on a row in the table
  metaData = [], // This is more data for specific usage.
  messageIfEmpty = "אין מידע", // This is the message to be displayed if the table has no data
  onFilteredDataChange, // Callback to pass filtered data to parent component
  className, // Additional class names
  ...props // Rest of the props
}) => {
  // State management for filtering and loading
  const [filters, setFilters] = useState({}); // Stores the current filter values
  const [activeFilters, setActiveFilters] = useState({}); // Tracks which filters are currently active
  const [isLoading, setIsLoading] = useState(false); // Loading state for filter operations
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: "asc",
  }); // New state for sorting
  const [originalData, setOriginalData] = useState([]); // Store original transformed data
  const [displayData, setDisplayData] = useState([]); // Store data to display (filtered or original)

  // Ref to track previous filtered data for comparison
  const prevFilteredDataRef = useRef(null);
  const dataRef = useRef(data);

  // Define columns based on data and mapping
  const columns = React.useMemo(
    () =>
      Object.keys(columnMapping).concat(
        [...new Set(data.flatMap(Object.keys))].filter(
          (col) => !ignoredAttributes.includes(col) && !columnMapping[col]
        )
      ),
    [data, columnMapping, ignoredAttributes]
  );

  // Transform and store original data when data prop changes
  useEffect(() => {
    // Create a deep copy of the data to ensure we don't modify the original
    const transformed = JSON.parse(JSON.stringify(data))
      .filter((row) => {
        if (type === "students" && row.status === "toReview") return false;
        return true;
      })
      .map((row) => {
        if (type === "groups") {
          return { ...row };
        }

        if ((type === "students" || type === "newstudents") && row.groupsIDs) {
          // Handle multiple groups
          const groupIDs = Array.isArray(row.groupsIDs)
            ? row.groupsIDs
            : [row.groupsIDs];
          const groups = groupIDs
            .map((id) => metaData?.find((group) => group.id === id))
            .filter((group) => group); // Remove any undefined groups

          const displayGroups =
            groups.length > 0
              ? groups
                  .map((group) => `${group.name}, סניף ${group.dojo}`)
                  .join(" | ")
              : "-";

          return {
            ...row,
            groupsIDs: groupIDs,
            displayGroups: displayGroups,
          };
        }
        return row;
      });

    // Only update state if the transformed data is actually different
    const isEqual = (a, b) => {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) return false;
      }
      return true;
    };

    if (!isEqual(transformed, originalData)) {
      setOriginalData(transformed);
      setDisplayData(transformed);
    }
  }, [data, type, metaData]);

  // Debounced filter handler - prevents excessive re-renders during typing
  const debouncedFilter = useCallback(
    debounce((column, value) => {
      setFilters((prev) => ({ ...prev, [column]: value.toLowerCase() }));
      setActiveFilters((prev) => ({ ...prev, [column]: value !== "" }));
      setIsLoading(false);
    }, 300),
    []
  );

  // Handle filter input changes
  const handleFilterChange = useCallback(
    (column, value) => {
      setIsLoading(true);
      if (!value.trim()) {
        // If the filter value is empty, remove the filter for this column
        setFilters((prev) => {
          const newFilters = { ...prev };
          delete newFilters[column];
          return newFilters;
        });
        setActiveFilters((prev) => {
          const newActiveFilters = { ...prev };
          delete newActiveFilters[column];
          return newActiveFilters;
        });
        setIsLoading(false);
      } else {
        debouncedFilter(column, value);
      }
    },
    [filters, debouncedFilter, type]
  );

  // Apply filtering when filters change
  useEffect(() => {
    if (!originalData.length) return; // Skip if no original data

    if (Object.keys(filters).length === 0) {
      // If no filters are active, show original data
      setDisplayData([...originalData]); // Create a new array to trigger re-render
    } else {
      // Apply filters to original data
      const newFilteredData = originalData.filter((row) =>
        columns.every(
          (col) =>
            !filters[col] ||
            (row[col] && String(row[col]).toLowerCase().includes(filters[col]))
        )
      );
      setDisplayData(newFilteredData);
    }
  }, [filters, originalData, columns, type]);

  // Notify parent component about filtered data changes
  useEffect(() => {
    if (!displayData.length) return; // Skip if no display data

    if (onFilteredDataChange) {
      // Check if the filtered data has actually changed
      const hasChanged =
        !prevFilteredDataRef.current ||
        prevFilteredDataRef.current.length !== displayData.length ||
        prevFilteredDataRef.current.some((item, index) => {
          const newItem = displayData[index];
          return !newItem || item.id !== newItem.id;
        });

      if (hasChanged) {
        // Only notify parent about the filtered data, don't update the original data
        onFilteredDataChange([...displayData]); // Send a copy of the filtered data
        prevFilteredDataRef.current = displayData;
      }
    }
  }, [displayData, onFilteredDataChange, type]);

  // Sort the display data based on current sort configuration
  const sortedData = React.useMemo(
    () =>
      [...displayData].sort((a, b) => {
        if (!sortConfig.column) return 0;

        const aValue = a[sortConfig.column];
        const bValue = b[sortConfig.column];

        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        const comparison = String(aValue).localeCompare(String(bValue), "he");
        return sortConfig.direction === "asc" ? comparison : -comparison;
      }),
    [displayData, sortConfig]
  );

  // Add sort handler function
  const handleSort = useCallback(
    (column) => {
      let direction = "asc";
      if (sortConfig.column === column && sortConfig.direction === "asc") {
        direction = "desc";
      }
      setSortConfig({ column, direction });
    },
    [sortConfig]
  );

  // Get sort icon for column header
  const getSortIcon = useCallback(
    (column) => {
      if (sortConfig.column !== column) return <FaSort />;
      return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
    },
    [sortConfig]
  );

  // Clear filters handler
  const handleClearFilters = useCallback(() => {
    setFilters({});
    setActiveFilters({});
    setDisplayData([...originalData]); // Reset to original data with a new array
  }, [originalData]);

  // Determine the appropriate input type for each column
  const getFilterType = useCallback((column, value) => {
    if (typeof value === "number") return "number";
    if (value instanceof Date) return "date";
    return "text";
  }, []);

  // This function fetches the group data by the group id, it is used to translate groups ID numbers to group names
  const getGroupByID = (groupID) => {
    if (Array.isArray(groupID)) {
      return metaData?.find((group) => group.id === groupID[0]) || null;
    }
    return metaData?.find((group) => group.id === groupID) || null;
  };

  // Helper to load Hebrew font dynamically (DavidLibre)
  const loadHebrewFont = async (doc) => {
    // Helper function to convert ArrayBuffer to Base64
    const arrayBufferToBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    };

    // Load Regular Font
    const regularResponse = await fetch("/DavidLibre-Regular.ttf");
    const regularFontData = await regularResponse.arrayBuffer();
    const regularBase64 = arrayBufferToBase64(regularFontData);
    doc.addFileToVFS("DavidLibre-Regular.ttf", regularBase64);
    doc.addFont("DavidLibre-Regular.ttf", "DavidLibre", "normal");

    // Load Bold Font
    const boldResponse = await fetch("/DavidLibre-Bold.ttf");
    const boldFontData = await boldResponse.arrayBuffer();
    const boldBase64 = arrayBufferToBase64(boldFontData);
    doc.addFileToVFS("DavidLibre-Bold.ttf", boldBase64);
    doc.addFont("DavidLibre-Bold.ttf", "DavidLibre", "bold");
  };

  // Generate PDF report of the current table data
  const generatePDF = useCallback(
    async (tableData) => {
      if (!tableData || tableData.length === 0) {
        alert("אין נתונים לייצוא");
        return;
      }

      const doc = new jsPDF();
      await loadHebrewFont(doc);
      doc.setFont("DavidLibre", "bold");
      doc.setR2L(true);

      // Set document properties
      doc.setProperties({
        title: `דוח ${
          type === "students"
            ? "תלמידים"
            : type === "groups"
            ? "קבוצות"
            : "נתונים"
        }`,
        subject: "דוח מהיר",
        author: "SimplyCoach",
        creator: "SimplyCoach Web App",
      });

      // Add title
      doc.setFontSize(18);
      doc.text("דוח מהיר", 105, 20, { align: "center" });

      // Add subtitle with data type
      doc.setFontSize(14);
      const subtitle =
        type === "students"
          ? "תלמידים"
          : type === "groups"
          ? "קבוצות"
          : type === "newstudents"
          ? "תלמידים חדשים"
          : "נתונים";
      doc.text(subtitle, 105, 30, { align: "center" });

      // Add date
      doc.setFontSize(10);
      const currentDate = new Date().toLocaleDateString("he-IL");
      doc.text(`תאריך הפקה: ${currentDate}`, 105, 40, { align: "center" });

      // Prepare table data
      const tableColumns = columns.map((col) => ({
        header: columnMapping[col] || col,
        dataKey: col,
      }));

      // Helper function to format cell values for PDF (plain text only)
      const formatCellValueForPDF = (value, col = "") => {
        // If no value, we render accordingly
        if (value === undefined || value === null) return "-";

        // Specific Scenario Handle - If it's a data table of groups, and there are multiple training days, we render it visually
        if (typeof value === "object" && type === "groups") {
          return value
            .map((slot) => `יום ${slot.day}' בשעות ${slot.range}`)
            .join("\n");
        }

        if (type === "newstudents" && col === "paymentCheckData") {
          if (value.InvoiceString && value.creditCardDigits) {
            return `מספר חשבונית: ${value.InvoiceString}\nספרות אחרונות של כרטיס אשראי: ${value.creditCardDigits}`;
          } else {
            return "-";
          }
        }

        if (type === "newstudents" && col === "studentCreatedAt") {
          return value
            ? new Date(value.seconds * 1000)
                .toLocaleString("he-IL", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                .replace(",", "")
            : "-";
        }

        if (type === "newstudents" && col === "groupsIDs") {
          const group = getGroupByID(value);
          return `${group?.name}, סניף ${group?.dojo}` || "-";
        }

        // Special handling for displayGroups to render multiple groups nicely in PDF
        if (col === "displayGroups" && value && value !== "-") {
          const groups = value.split(" | ");
          return groups.join("\n");
        }

        return String(value);
      };

      const tableRows = tableData.map((row) =>
        columns.map((col) => formatCellValueForPDF(row[col], col))
      );

      // Generate table
      autoTable(doc, {
        head: [tableColumns.map((col) => col.header)],
        body: tableRows,
        startY: 50,
        styles: {
          font: "DavidLibre",
          fontSize: 8,
          cellPadding: 3,
          textColor: [0, 0, 0],
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
          halign: "right",
          direction: "rtl",
        },
        headStyles: {
          fillColor: [70, 130, 180],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { top: 50, right: 10, bottom: 10, left: 10 },
        didDrawPage: function (data) {
          // Add page number
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(
            `עמוד ${data.pageNumber} מתוך ${pageCount}`,
            105,
            doc.internal.pageSize.height - 10,
            { align: "center" }
          );
        },
      });

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `דוח_${subtitle}_${timestamp}.pdf`;

      // Save the PDF
      doc.save(filename);
    },
    [columns, columnMapping, type, metaData]
  );

  const formatCellValue = (value, col = "") => {
    // If no value, we render accordingly
    if (value === undefined || value === null) return "-";
    // Specific Scenarion Handle - If it's a data table of groups, and there are multiple training days, we render it visually
    if (typeof value === "object" && type === "groups") {
      return value
        .map((slot) => `יום ${slot.day}' בשעות ${slot.range}`)
        .join("\n");
    }

    if (type === "newstudents" && col === "paymentCheckData") {
      if (value.InvoiceString && value.creditCardDigits) {
        return (
          <div>
            מספר חשבונית: {value.InvoiceString}
            <br />
            ספרות אחרונות של כרטיס אשראי: {value.creditCardDigits}
          </div>
        );
      } else {
        return "-";
      }
    }

    if (type === "newstudents" && col === "studentCreatedAt") {
      return value
        ? new Date(value.seconds * 1000)
            .toLocaleString("he-IL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
            .replace(",", "")
        : "-";
    }

    if (type === "newstudents" && col === "groupsIDs") {
      const group = getGroupByID(value);
      return `${group?.name}, סניף ${group?.dojo}` || "-";
    }

    // Special handling for displayGroups to render multiple groups nicely
    if (col === "displayGroups" && value && value !== "-") {
      const groups = value.split(" | ");
      return (
        <div>
          {groups.map((group, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "#e9ecef",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "1em",
                whiteSpace: "nowrap",
                marginBottom: index < groups.length - 1 ? "4px" : "0",
              }}
            >
              {group}
            </div>
          ))}
        </div>
      );
    }

    return String(value);
  };

  // Early return if no data is available at all
  if (!Array.isArray(data)) {
    return (
      <div className="data-table-empty">
        <p>{messageIfEmpty}</p>
      </div>
    );
  }

  const hasActiveFilters = Object.values(activeFilters).some(Boolean);

  return (
    <div className="data-table-container" {...props}>
      <div className="data-table-header">
        <div className="data-table-actions">
          <button
            className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Toggle Filters"
          >
            <FaFilter /> סינון
          </button>
          {hasActiveFilters && (
            <button
              className="clear-filters-btn"
              onClick={handleClearFilters}
              title="Clear Filters"
            >
              <FaTimes /> נקה סינון
            </button>
          )}
          <button
            className="export-pdf-btn"
            onClick={() => generatePDF(sortedData)}
            title="Export to PDF"
          >
            <FaFilePdf /> הפקת דוח מהיר
          </button>
        </div>
      </div>

      <div className={`table-responsive ${className || ""}`}>
        <Table className={`data-table data-table-${type}`}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                  className="sortable-header"
                  style={{ cursor: "pointer" }}
                >
                  <div className="column-header">
                    <span>{columnMapping[col] || col}</span>
                    <span className="sort-icon">{getSortIcon(col)}</span>
                    {activeFilters[col] && <FaFilter className="filter-icon" />}
                  </div>
                </th>
              ))}
            </tr>
            {showFilters && (
              <tr className="filter-row">
                {columns.map((col) => (
                  <th key={col}>
                    <div className="filter-input-container">
                      <FaSearch className="search-icon" />
                      <Form.Control
                        type={getFilterType(col, data[0]?.[col])}
                        placeholder="סנן..."
                        onChange={(e) =>
                          handleFilterChange(col, e.target.value)
                        }
                        className={activeFilters[col] ? "active" : ""}
                        dir="rtl"
                      />
                    </div>
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <span>טוען...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="no-results">
                    <p>{messageIfEmpty}</p>
                  </div>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className="no-results">
                    <p>לא נמצאו תוצאות מתאימות</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  onClick={() => onRowClick?.(row)}
                  className={onRowClick ? "clickable" : ""}
                >
                  {columns.map((col) => (
                    <td
                      key={col}
                      className={rowIndex % 2 ? "row-alternate" : ""}
                    >
                      {formatCellValue(row[col], col)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DataTable;
